"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

interface UIContextType {
  // Navigation state
  globalNavExpanded: boolean;
  isHomePage: boolean;
  openSubmenuId: string | null;
  submenuPosition: { top: number; left: number } | null;

  // Modal state
  openModals: Set<string>;

  // Dropdown state
  openDropdowns: Set<string>;

  // Actions
  setGlobalNavExpanded: (expanded: boolean) => void;
  setIsHomePage: (isHomePage: boolean) => void;
  setOpenSubmenuId: (id: string | null) => void;
  setSubmenuPosition: (position: { top: number; left: number } | null) => void;
  openModal: (id: string) => void;
  closeModal: (id: string) => void;
  openDropdown: (id: string) => void;
  closeDropdown: (id: string) => void;
  closeSubmenu: () => void;

  // Computed
  isModalOpen: (id: string) => boolean;
  isDropdownOpen: (id: string) => boolean;
}

export const UIContext = createContext<UIContextType | undefined>(undefined);

/**
 * Hook to access UI context
 */
export function useUI() {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
}

/**
 * UIProvider - Manages global UI state
 */
export function UIProvider({ children }: { children: React.ReactNode }) {
  const [globalNavExpanded, setGlobalNavExpanded] = useState(true);
  const [isHomePage, setIsHomePage] = useState(false);
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set());

  const openModal = useCallback((id: string) => {
    setOpenModals((prev) => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  }, []);

  const closeModal = useCallback((id: string) => {
    setOpenModals((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const openDropdown = useCallback((id: string) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      newSet.add(id);
      return newSet;
    });
  }, []);

  const closeDropdown = useCallback((id: string) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const closeSubmenu = useCallback(() => {
    setOpenSubmenuId(null);
    setSubmenuPosition(null);
  }, []);

  const isModalOpen = useCallback(
    (id: string) => {
      return openModals.has(id);
    },
    [openModals],
  );

  const isDropdownOpen = useCallback(
    (id: string) => {
      return openDropdowns.has(id);
    },
    [openDropdowns],
  );

  const value = useMemo(
    () => ({
      globalNavExpanded,
      isHomePage,
      openSubmenuId,
      submenuPosition,
      openModals,
      openDropdowns,
      setGlobalNavExpanded,
      setIsHomePage,
      setOpenSubmenuId,
      setSubmenuPosition,
      openModal,
      closeModal,
      openDropdown,
      closeDropdown,
      closeSubmenu,
      isModalOpen,
      isDropdownOpen,
    }),
    [
      globalNavExpanded,
      isHomePage,
      openSubmenuId,
      submenuPosition,
      openModals,
      openDropdowns,
      setGlobalNavExpanded,
      setIsHomePage,
      setOpenSubmenuId,
      setSubmenuPosition,
      openModal,
      closeModal,
      openDropdown,
      closeDropdown,
      closeSubmenu,
      isModalOpen,
      isDropdownOpen,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}
